import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  ShieldAlert,
  TimerReset,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Ban,
  Eye,
} from 'lucide-react';

import { piiAPI } from '../services/api';
import config from '../config';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function fieldLabel(field) {
  return String(field || '')
    .replaceAll('_', ' ')
    .replaceAll('.', ' › ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function SensitivityBadge({ sensitivity }) {
  const value = String(sensitivity || 'UNKNOWN').toUpperCase();

  return (
    <span
      className={cx(
        'rounded-full px-2 py-0.5 text-[11px] font-bold',
        value === 'CRITICAL'
          ? 'bg-red-50 text-red-700'
          : value === 'HIGH'
            ? 'bg-orange-50 text-orange-700'
            : value === 'MEDIUM'
              ? 'bg-blue-50 text-blue-700'
              : 'bg-slate-100 text-slate-600',
      )}
    >
      {value}
    </span>
  );
}

function getBackendBaseUrl() {
  const apiBase = String(config.API_BASE_URL || '').replace(/\/$/, '');

  // Example:
  // config.API_BASE_URL = http://localhost:9000/api
  // backendBaseUrl       = http://localhost:9000
  if (apiBase.endsWith('/api')) {
    return apiBase.slice(0, -4);
  }

  return apiBase;
}

export default function PIIViewerModal({
  recordUuid,
  recordId,
  piiRecordUuid,
  consentId,
  accessScope = 'active',
  onClose,
}) {
  const resolvedRecordUuid = recordUuid || piiRecordUuid || recordId || '';
  const [phase, setPhase] = useState('configure');
  const [error, setError] = useState('');
  const [iframeSrc, setIframeSrc] = useState('');

  const [timeRemaining, setTimeRemaining] = useState(30);
  const [expiresInSeconds, setExpiresInSeconds] = useState(30);

  const [fieldInventory, setFieldInventory] = useState(null);
  const [fieldInventoryLoading, setFieldInventoryLoading] = useState(true);

  const [viewMode, setViewMode] = useState('masked');
  const [accessReason, setAccessReason] = useState(
    accessScope === 'retained' ? 'Governed retained-data review' : 'Operational PII review',
  );
  const [justification, setJustification] = useState('');
  const [purposeCode, setPurposeCode] = useState('');

  const [selectedFields, setSelectedFields] = useState([]);
  const [requestAllFields, setRequestAllFields] = useState(true);
  const [fieldAccess, setFieldAccess] = useState(null);

  const iframeRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fields = useMemo(() => {
    return fieldInventory?.fields || [];
  }, [fieldInventory]);

  const accessPolicy = fieldInventory?.access_policy || {};
  const allowedPurposes = Array.isArray(accessPolicy.allowed_purpose_codes)
    ? accessPolicy.allowed_purpose_codes
    : [];
  const allowFullView = Boolean(accessPolicy.allow_full_view);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    clearTimer();
    if (iframeRef.current) iframeRef.current.src = 'about:blank';
    setIframeSrc('');
    onClose();
  }, [clearTimer, onClose]);

  const startCountdown = useCallback((ttl) => {
    const safeTtl = Number(ttl || 30);
    setExpiresInSeconds(safeTtl);
    setTimeRemaining(safeTtl);

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearTimer();
          if (mountedRef.current) setPhase('expired');
          if (iframeRef.current) iframeRef.current.src = 'about:blank';
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, [clearTimer]);

  useEffect(() => {
    mountedRef.current = true;

    async function loadFields() {
      setFieldInventoryLoading(true);
      setError('');

      try {
        if (!resolvedRecordUuid) {
          setError('PII record UUID is missing. Please reopen the record from the PII Records list.');
          setFieldInventoryLoading(false);
          return;
        }

        const response = await piiAPI.getRecordFields(resolvedRecordUuid);

        if (!mountedRef.current) return;

        const inventory = response.data || null;
        setFieldInventory(inventory);

        const policy = inventory?.access_policy || {};
        const purposes = Array.isArray(policy.allowed_purpose_codes)
          ? policy.allowed_purpose_codes.filter((value) => value && value !== '*')
          : [];
        if (purposes.length) setPurposeCode((current) => current || purposes[0]);
        if (!policy.allow_full_view) setViewMode('masked');
        else if (policy.default_view_mode === 'full') setViewMode('full');
      } catch (err) {
        if (!mountedRef.current) return;

        setError(
          err.response?.data?.detail ||
            err.message ||
            'Failed to load PII field inventory.',
        );
      } finally {
        if (mountedRef.current) {
          setFieldInventoryLoading(false);
        }
      }
    }

    loadFields();

    return () => {
      mountedRef.current = false;
      clearTimer();
      if (iframeRef.current) iframeRef.current.src = 'about:blank';
    };
 }, [resolvedRecordUuid, clearTimer]);

  const toggleField = (fieldName) => {
    setSelectedFields((prev) => {
      if (prev.includes(fieldName)) {
        return prev.filter((item) => item !== fieldName);
      }

      return [...prev, fieldName];
    });
  };

  const startSecureView = async () => {
    setError('');

    if (!accessReason.trim()) {
      setError('Access reason is required.');
      return;
    }

    if (!justification.trim()) {
      setError('Justification is required.');
      return;
    }

    setPhase('requesting');

    try {
      const requestedFields = requestAllFields ? [] : selectedFields;

      if (!resolvedRecordUuid) {
        setError('PII record UUID is missing. Cannot start secure view.');
        return;
      }

      const response = await piiAPI.requestPIIView(resolvedRecordUuid, {
        view_mode: viewMode,
        access_reason: accessReason,
        justification,
        purpose_code: purposeCode,
        requested_fields: requestedFields,
        access_scope: accessScope,
      });

      if (!mountedRef.current) return;

      const token = response.data?.token;
      const ttl = response.data?.expires_in_seconds || 30;

      if (!token) {
        throw new Error('PII view token missing from response.');
      }

      setFieldAccess(response.data?.field_access || null);

      const backendBaseUrl = getBackendBaseUrl();

      const src = `${backendBaseUrl}/api/pii/pii-records/view/?token=${encodeURIComponent(token)}`;

      setIframeSrc(src);
      setPhase('viewing');
      startCountdown(ttl);
    } catch (err) {
      if (!mountedRef.current) return;

      setError(
        err.response?.data?.detail ||
          err.message ||
          'Failed to start secure view session.',
      );
      setPhase('configure');
    }
  };

  const timeText = (() => {
    const m = Math.floor(timeRemaining / 60);
    const s = String(timeRemaining % 60).padStart(2, '0');
    return `${m}:${s}`;
  })();

  const timerUrgent = timeRemaining <= 10;

  const returnedFields = fieldAccess?.returned_fields || [];
  const maskedFields = fieldAccess?.masked_fields || [];
  const deniedFields = fieldAccess?.denied_fields || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="relative mx-4 flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ maxHeight: '92vh' }}
      >
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <ShieldAlert className="h-3.5 w-3.5" />
              Secure PII View
            </span>

            {consentId && (
              <span className="font-mono text-xs text-gray-400">{consentId}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {phase === 'viewing' && (
              <span
                className={cx(
                  'inline-flex items-center gap-1.5 font-mono text-sm font-medium',
                  timerUrgent ? 'text-red-600' : 'text-gray-500',
                )}
              >
                <TimerReset className="h-4 w-4" />
                {timeText}
              </span>
            )}

            <button
              onClick={handleClose}
              className="rounded-lg p-1.5 transition-colors hover:bg-gray-200"
              title="Close"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto" style={{ minHeight: '520px' }}>
          {phase === 'configure' && (
            <div className="grid gap-6 p-6 lg:grid-cols-[1fr_360px]">
              <div className="space-y-5">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <div className="flex gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4" />
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {accessScope === 'retained' && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        This consent is revoked. Only fields lawfully retained under the
                        approved retention rule are available. The reason, purpose, actor,
                        fields viewed, and session expiry are recorded in the audit trail.
                      </p>
                    </div>
                  </div>
                )}

                <section className="rounded-2xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-900">Access details</h3>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase text-slate-500">
                        View mode
                      </label>
                      <select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      >
                        <option value="masked">Masked</option>
                        {allowFullView && <option value="full">Full</option>}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase text-slate-500">
                        Purpose code
                      </label>
                      {allowedPurposes.length > 0 && !allowedPurposes.includes('*') ? (
                        <select
                          value={purposeCode}
                          onChange={(e) => setPurposeCode(e.target.value)}
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        >
                          <option value="">Select purpose</option>
                          {allowedPurposes.map((purpose) => (
                            <option key={purpose} value={purpose}>
                              {String(purpose).replaceAll('_', ' ')}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={purposeCode}
                          onChange={(e) => setPurposeCode(e.target.value)}
                          placeholder="Purpose code"
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        />
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase text-slate-500">
                        Access reason
                      </label>
                      <input
                        value={accessReason}
                        onChange={(e) => setAccessReason(e.target.value)}
                        placeholder="Operational PII review"
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase text-slate-500">
                        Justification
                      </label>
                      <textarea
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        placeholder="Explain why this PII access is needed."
                        rows={3}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">Requested fields</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Backend policy is still authoritative. Selection only requests fields.
                      </p>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={requestAllFields}
                        onChange={(e) => setRequestAllFields(e.target.checked)}
                      />
                      Request all
                    </label>
                  </div>

                  <div className="mt-4">
                    {fieldInventoryLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading fields…
                      </div>
                    ) : fields.length === 0 ? (
                      <p className="text-sm text-slate-500">No field inventory found.</p>
                    ) : (
                      <div className="grid gap-2 md:grid-cols-2">
                        {fields.map((field) => (
                          <label
                            key={field.normalized_field_name}
                            className={cx(
                              'flex cursor-pointer items-start gap-3 rounded-xl border p-3',
                              requestAllFields
                                ? 'border-slate-100 bg-slate-50 opacity-60'
                                : selectedFields.includes(field.normalized_field_name)
                                  ? 'border-orange-300 bg-orange-50'
                                  : 'border-slate-200 bg-white',
                            )}
                          >
                            <input
                              type="checkbox"
                              disabled={requestAllFields}
                              checked={
                                requestAllFields ||
                                selectedFields.includes(field.normalized_field_name)
                              }
                              onChange={() => toggleField(field.normalized_field_name)}
                              className="mt-1"
                            />

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-800">
                                {fieldLabel(field.field_name)}
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span className="text-xs text-slate-500">
                                  {field.field_type || 'UNKNOWN'}
                                </span>
                                <SensitivityBadge sensitivity={field.sensitivity} />
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <aside className="space-y-4">
                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-semibold text-slate-900">Field summary</h3>

                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Total fields</span>
                      <span className="font-semibold">{fieldInventory?.field_count || 0}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-500">Sensitive fields</span>
                      <span className="font-semibold">
                        {fieldInventory?.sensitive_field_count || 0}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-500">Highest sensitivity</span>
                      <SensitivityBadge sensitivity={fieldInventory?.highest_sensitivity} />
                    </div>
                  </div>
                </section>

                <button
                  onClick={startSecureView}
                  disabled={phase === 'requesting'}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {phase === 'requesting' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting secure view…
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Start secure view
                    </>
                  )}
                </button>
              </aside>
            </div>
          )}

          {phase === 'requesting' && (
            <div className="flex h-[520px] flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              <p className="text-sm">Starting secure session…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex h-[520px] flex-col items-center justify-center gap-3 px-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <p className="text-sm font-medium text-gray-800">Session failed</p>
              <p className="text-xs text-gray-500">{error}</p>
              <button
                onClick={handleClose}
                className="mt-2 rounded-lg bg-gray-100 px-4 py-2 text-sm transition-colors hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}

          {phase === 'expired' && (
            <div className="flex h-[520px] flex-col items-center justify-center gap-3 bg-white px-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <TimerReset className="h-6 w-6 text-amber-500" />
              </div>
              <p className="text-sm font-medium text-gray-800">Session expired</p>
              <p className="text-xs text-gray-500">
                This view has timed out. Request a new session to view PII data again.
              </p>
              <button
                onClick={handleClose}
                className="mt-2 rounded-lg bg-gray-100 px-4 py-2 text-sm transition-colors hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}

          {phase === 'viewing' && (
            <div className="grid h-full min-h-[520px] grid-cols-1 lg:grid-cols-[1fr_320px]">
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                title="Secure PII Viewer"
                className="h-full min-h-[520px] w-full border-0"
                sandbox="allow-same-origin"
                referrerPolicy="no-referrer"
                onContextMenu={(e) => e.preventDefault()}
              />

              <aside className="border-l border-slate-200 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-900">Field decision</h3>

                <div className="mt-4 space-y-3 text-xs">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="mb-2 flex items-center gap-1.5 font-bold text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Returned
                    </div>
                    {returnedFields.length ? returnedFields.map((field) => (
                      <p key={field}>{fieldLabel(field)}</p>
                    )) : <p>—</p>}
                  </div>

                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                    <div className="mb-2 flex items-center gap-1.5 font-bold text-blue-700">
                      <Eye className="h-3.5 w-3.5" />
                      Masked
                    </div>
                    {maskedFields.length ? maskedFields.map((field) => (
                      <p key={field}>{fieldLabel(field)}</p>
                    )) : <p>—</p>}
                  </div>

                  <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                    <div className="mb-2 flex items-center gap-1.5 font-bold text-red-700">
                      <Ban className="h-3.5 w-3.5" />
                      Denied
                    </div>
                    {deniedFields.length ? deniedFields.map((field) => (
                      <p key={field}>{fieldLabel(field)}</p>
                    )) : <p>—</p>}
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3">
          <p className="text-xs text-gray-400">
            🔒 Decrypted server-side · Field-policy enforced · One-time token · Not cached
          </p>

          {phase === 'viewing' && (
            <div
              className={cx(
                'h-1.5 rounded-full transition-all duration-1000',
                timerUrgent ? 'bg-red-400' : 'bg-emerald-400',
              )}
              style={{
                width: `${(timeRemaining / expiresInSeconds) * 180}px`,
                maxWidth: '180px',
                minWidth: '0px',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}